import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store';
import FilterSelect from '@/components/filters/FilterSelect';
import Search from '@/components/filters/Search';
import { Admin } from './types';
import RoleTable from './components/RoleTable';
import { fetchUsersWithRoleAndPermission } from '@/store/Admin';
import InviteButton from '@/components/buttons/InviteUserButton';
import InviteUserModal from './components/InviteUserModal';
import AuthService from '@/services/AuthService';
import { toast } from 'react-toastify';
import EditRoleAndPermissionModal from './components/EditRoleAndPermissionModal';

const mockCats: string[] = ['All', 'admin', 'user', 'publisher', 'moderator'];

const Role: React.FC = () => {
  const [role, setRole] = useState('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [newInvite, setNewInvite] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [userData, setUserData] = useState<Admin | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const {
    usersWithRole = [],
    usersWithRolePagination,
    status: sliceStatus,
  } = useSelector((s: RootState) => s.admin);

  const totalPages = usersWithRolePagination?.totalPages ?? 1;
  const rows = useMemo(
    () => (Array.isArray(usersWithRole) ? usersWithRole : []),
    [usersWithRole],
  );

  /* ─── first load & filters change ───────────────────────────────────────── */
  useEffect(() => {
    setPage(1); // Reset to page 1 on filter change
    dispatch(
      fetchUsersWithRoleAndPermission({
        name: search || undefined,
        role: role !== 'All' ? role : undefined,
        page: 1,
        limit: 20,
      }),
    );
  }, [dispatch, search, role]);

  /* ─── infinite-scroll callback ──────────────────────────────────────────── */
  const loadMore = () => {
    if (page >= totalPages || sliceStatus === 'loading') return;
    const next = page + 1;
    setPage(next);
    dispatch(
      fetchUsersWithRoleAndPermission({
        name: search || undefined,
        role: role !== 'All' ? role : undefined,
        page: next,
        limit: 20,
      }),
    );
  };

  /* ─── map API → table model ─────────────────────────────────────────────── */
  const formatData = (dataArr: any[]): Admin[] =>
    dataArr.map((g) => ({
      id: g.id,
      name: g.name,
      role: Array.isArray(g.roles) ? g.roles.join(', ') : '',
      email: g.email || '',
      permission: Array.isArray(g.permissions) ? g.permissions : [],
      lastPermissionActivity: g.last_permission_activity ?? null,
    }));

  return (
    <div className='p-8 rounded-[24px] bg-white flex flex-col h-full min-h-0 space-y-[24px]'>
      {/* ─── header & filters ───────────────────────────────────────────── */}
      <div className='flex items-center gap-2 justify-between'>
        <div className='text-[26px] font-[600]'>
          Showing {rows.length} Results
        </div>

        <div className='flex-wrap inline-flex items-center gap-2 lg:gap-0 space-x-[10px]'>
          <Search value={search} onChange={(e) => setSearch(e.target.value)} />
          <FilterSelect
            value={role}
            onChange={(e) => setRole(e.target.value)}
            options={[
              { value: 'All', label: 'Role: All' },
              ...mockCats
                .filter((c) => c !== 'All')
                .map((c) => ({ value: c, label: `Role: ${c}` })),
            ]}
          />
          <div className='ml-auto w-[202px] inline-flex items-center'>
            <InviteButton
              onClick={() => setNewInvite(true)}
              label='Invite New User'
              responsive={false}
            />
          </div>
        </div>
      </div>

      {/* ─── table ──────────────────────────────────────────────────────── */}
      <div className='flex-1 min-h-0'>
        <RoleTable
          data={formatData(rows)}
          loadMore={loadMore}
          hasMore={page < totalPages}
          setIsOpen={setIsOpen}
          setUserData={setUserData}
        />
      </div>
      {
        <InviteUserModal
          isOpen={newInvite}
          onClose={() => setNewInvite(false)}
        />
      }
      {userData && (
        <EditRoleAndPermissionModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          user={userData}
        />
      )}
    </div>
  );
};

export default Role;
